// Throttling for telemetry to avoid spam
const telemetryThrottle = new Map<string, number>();
const THROTTLE_MS = 5000; // 5 seconds

export function track(event: string, payload: Record<string, any> = {}) {
  const { requestId, backend, forcedFallback, rateRemaining, rateReset, ms, ...rest } = payload
  
  // Throttling check
  const throttleKey = `${event}:${JSON.stringify(rest)}`;
  const now = Date.now();
  const lastSent = telemetryThrottle.get(throttleKey);
  if (lastSent && (now - lastSent) < THROTTLE_MS) {
    return; // Skip throttled events
  }
  telemetryThrottle.set(throttleKey, now);
  
  // PII Guard - remove sensitive fields
  const sanitizedRest = Object.fromEntries(
    Object.entries(rest).filter(([key, value]) => {
      const sensitiveFields = ['email', 'phone', 'name', 'address', 'user_id', 'customer_id'];
      return !sensitiveFields.some(field => key.toLowerCase().includes(field)) && 
             typeof value !== 'string' || !value.includes('@');
    })
  );
  
  // Auto-detect mode: TG if /tg route or initData present, otherwise Web
  const mode = (window.location.pathname.startsWith('/tg') || Boolean(window.Telegram?.WebApp?.initData)) ? 'tg' : 'web';
  
  const body = {
    event,
    ts: now,
    requestId: requestId || crypto.randomUUID?.(),
    backend,
    forcedFallback: Boolean(forcedFallback),
    rateRemaining,
    rateReset,
    ms,
    mode, // TG vs Web segmentation for Phase D analytics
    // Enhanced metadata for fail-soft tracking - using import.meta.env for Vite
    env: import.meta.env.VITE_ENV_LABEL || 'unknown',
    buildSha: import.meta.env.VITE_BUILD_SHA || import.meta.env.VITE_BUILD_TIME || 'unknown',
    source: 'client',
    ...sanitizedRest,
  }
  
  // Enhanced logging for production debugging
  if (import.meta.env.VITE_ENV_LABEL === 'PRD') {
    console.info('[track:prod]', { event, ...body })
  } else {
    console.debug('[track]', body)
  }
  
  // TODO: Send to actual telemetry service in production
  // if (import.meta.env.VITE_ENABLE_TELEMETRY === 'true') {
  //   fetch('/api/telemetry', {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify(body)
  //   }).catch(err => console.warn('[track] Failed to send:', err));
  // }
}

// UX Polish telemetry events (Phase D: CTA tracking)
export const UX_EVENTS = {
  SKELETON: 'search_skeleton_shown',
  EMPTY: 'search_empty_state', 
  HEADER_USED: 'search_header_used',
  MICRO_HOVER: 'search_micro_hover',
  CTA_HOVER_CYCLE_START: 'cta_hover_cycle_start',
  MAGICLINK_SUCCESS: 'magiclink_success',
  TG_AUTH_START: 'tg_auth_start',
  TG_AUTH_SUCCESS: 'tg_auth_success',
  DASHBOARD_LAND: 'dashboard_land',
} as const;

// Admin degradation tracking events
export const ADMIN_EVENTS = {
  RPC_FALLBACK: 'admin_rpc_fallback',
  RPC_ERROR: 'admin_rpc_error', 
  RPC_EXCEPTION: 'admin_rpc_exception',
  RPC_BODY_ERROR: 'admin_rpc_body_error',
  SERVICE_DEGRADED: 'admin_service_degraded',
  FAIL_SOFT_TRIGGERED: 'admin_fail_soft_triggered',
} as const;